import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Loader2, Eye, EyeOff, StickyNote, Pencil, Check, X } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "flights", label: "Flights" },
  { value: "accommodation", label: "Accommodation" },
  { value: "transport", label: "Transport" },
  { value: "tour_guide", label: "Tour Guide" },
  { value: "activities", label: "Activities / Experiences" },
  { value: "restaurants", label: "Restaurants / Dining" },
  { value: "meeting_points", label: "Meeting Points & Times" },
  { value: "reservations", label: "Reservation References" },
  { value: "visa_documents", label: "Visa / Documents" },
  { value: "payments", label: "Payments" },
  { value: "emergency_contacts", label: "Emergency Contacts" },
  { value: "special_requests", label: "Special Requests" },
  { value: "general", label: "General" },
];


interface ClientNoteRow {
  id: string;
  title: string;
  content: string;
  client_note_category: string | null;
  shared_with_client: boolean;
  shared_at: string | null;
  updated_at: string;
}

export function LeadClientNotes({ leadId }: { leadId: string }) {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("general");
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");

  const queryKey = ["lead-client-notes", leadId];

  const { data: notes, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notes")
        .select("id, title, content, client_note_category, shared_with_client, shared_at, updated_at")
        .eq("lead_id", leadId)
        .not("client_note_category", "is", null)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data || []) as unknown as ClientNoteRow[];
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey });

  const handleCreate = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error("Add a title and the note detail");
      return;
    }
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setSaving(false);
      return;
    }
    const { error } = await supabase.from("notes").insert({
      lead_id: leadId,
      user_id: user.id,
      title: title.trim(),
      content: content.trim(),
      client_note_category: category,
    } as any);
    setSaving(false);
    if (error) {
      toast.error("Could not save note", { description: error.message });
      return;
    }
    setTitle("");
    setContent("");
    setCategory("general");
    setCreating(false);
    refresh();
    toast.success("Note added");
  };

  const toggleShare = async (note: ClientNoteRow) => {
    setBusyId(note.id);
    const next = !note.shared_with_client;
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("notes")
      .update({
        shared_with_client: next,
        shared_at: next ? new Date().toISOString() : null,
        shared_by: next ? user?.id ?? null : null,
      } as any)
      .eq("id", note.id);
    setBusyId(null);
    if (error) {
      toast.error("Could not update sharing", { description: error.message });
      return;
    }
    refresh();
    toast.success(next ? "Shared with client" : "Hidden from client");
  };

  const updateCategory = async (note: ClientNoteRow, value: string) => {
    setBusyId(note.id);
    const { error } = await supabase
      .from("notes")
      .update({ client_note_category: value } as any)
      .eq("id", note.id);
    setBusyId(null);
    if (error) {
      toast.error("Could not update category");
      return;
    }
    refresh();
  };

  const startEdit = (note: ClientNoteRow) => {
    setEditingId(note.id);
    setEditTitle(note.title || "");
    setEditContent(note.content || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
    setEditContent("");
  };

  const saveEdit = async (note: ClientNoteRow) => {
    if (!editTitle.trim() || !editContent.trim()) {
      toast.error("Add a title and the note detail");
      return;
    }
    setBusyId(note.id);
    const { error } = await supabase
      .from("notes")
      .update({ title: editTitle.trim(), content: editContent.trim() } as any)
      .eq("id", note.id);
    setBusyId(null);
    if (error) {
      toast.error("Could not update note", { description: error.message });
      return;
    }
    cancelEdit();
    refresh();
    toast.success("Note updated");
  };

  const remove = async (note: ClientNoteRow) => {

    setBusyId(note.id);
    const { error } = await supabase.from("notes").delete().eq("id", note.id);
    setBusyId(null);
    if (error) {
      toast.error("Could not delete note");
      return;
    }
    refresh();
    toast.success("Note deleted");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <StickyNote className="h-4 w-4" />
            Client Notes
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Reservation numbers, flight references and instructions. Only notes marked as shared
            appear on the client dashboard.
          </p>
        </div>
        {!creating && (
          <Button size="sm" onClick={() => setCreating(true)} className="gap-1">
            <Plus className="h-4 w-4" /> Add Note
          </Button>
        )}
      </div>

      {creating && (
        <Card className="p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="note-title">Title</Label>
              <Input
                id="note-title"
                placeholder="e.g. Flight reference — SAA JNB/CPT"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="note-content">Detail</Label>
            <Textarea
              id="note-content"
              rows={4}
              placeholder="Reservation number, confirmation code, pickup instructions..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setCreating(false);
                setTitle("");
                setContent("");
              }}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={handleCreate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save Note
            </Button>
          </div>
        </Card>
      )}

      {isLoading ? (
        <div className="py-8 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : !notes || notes.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No client notes yet. Add reservation numbers and references here to share them on the
          client dashboard.
        </Card>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <Card key={note.id} className="p-4 space-y-3">
              {editingId === note.id ? (
                <div className="space-y-2">
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Note title"
                  />
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={4}
                    placeholder="Note detail"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => saveEdit(note)}
                      disabled={busyId === note.id}
                    >
                      {busyId === note.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={cancelEdit}>
                      <X className="h-4 w-4" /> Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm">{note.title || "Untitled note"}</p>
                      {note.shared_with_client ? (
                        <Badge variant="outline" className="text-xs gap-1 border-emerald-500/30 text-emerald-600">
                          <Eye className="h-3 w-3" /> Shared
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs gap-1 text-muted-foreground">
                          <EyeOff className="h-3 w-3" /> Internal
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-1">
                      {note.content}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Updated {format(new Date(note.updated_at), "MMM d, yyyy")}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => startEdit(note)}
                      disabled={busyId === note.id}
                      aria-label="Edit note"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive"
                      onClick={() => remove(note)}
                      disabled={busyId === note.id}
                      aria-label="Delete note"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-4 pt-2 border-t">
                <div className="flex items-center gap-2">
                  <Switch
                    id={`share-${note.id}`}
                    checked={note.shared_with_client}
                    onCheckedChange={() => toggleShare(note)}
                    disabled={busyId === note.id}
                  />
                  <Label htmlFor={`share-${note.id}`} className="text-xs">
                    Share with client
                  </Label>
                </div>
                <Select
                  value={note.client_note_category || "general"}
                  onValueChange={(v) => updateCategory(note, v)}
                >
                  <SelectTrigger className="h-8 w-[180px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                    {note.client_note_category &&
                      !CATEGORIES.some((c) => c.value === note.client_note_category) && (
                        <SelectItem value={note.client_note_category}>
                          {note.client_note_category} (existing)
                        </SelectItem>
                      )}
                  </SelectContent>

                </Select>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
